import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";

import { ReactComponent as Warning } from "../../../../assets/icons/error.svg";
import ErrorText from "../../../common/ErrorText/ErrorText";
import StyledModal from "../../../common/Modal/StyledModal";
import { typeOfPAF } from "../../../../constants/typeOfPAF";

import { errorMessages as errorMessagesConstants } from "../../../../constants/errorMessages.constants";
import {
  formatCapsulesPerCycleIncompleteTitle,
  formatCapsulesPerWeeksIncompleteTitle,
  formatOutsidePermittedQuantityTitle,
} from "../../../../shared/helpers";
import {
  isMaxCapsulesExceededPatientInitiationWeeksSchedule,
  isMaxWeeksExceededPatientInitiation,
} from "../../../../shared/pafComplianceChecks";

const NonComplaintPAF = ({
  openModalNonComplaint,
  closeNonComplaint,
  submitNonComplaint,
  registerPatient,
  prescriptionField,
  handleChangeValidPassword,
  errorMessageFromServer,
  hcp,
  displayWeeks,
}) => {
  let customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "37%",
      borderRadius: "30px",
    },
  };

  const mediaMatch = window.matchMedia("(max-width: 850px)");
  mediaMatch.matches === true
    ? (customStyles.content = {
      ...customStyles.content,
      width: "70%",
    })
    : (customStyles.content = {
      ...customStyles.content,
      width: "37%",
    });

  let errorMessages = [];

  if (registerPatient.continueOffLabel) {
    errorMessages.push("Continue with off-label use");
  }
  if (registerPatient.continueIndicationIncomplete) {
    errorMessages.push(errorMessagesConstants.INDICATION_INCOMPLETE_TITLE);
  }
  if (
    registerPatient.continueWithoutConfirmation &&
    registerPatient.type !== typeOfPAF.WOMANOFNCP
  ) {
    errorMessages.push("Patient counselling not confirmed");
  }
  if (
    registerPatient.continueWithPregnancyTestDateOutdated &&
    registerPatient.type === typeOfPAF.WOMANOFCP
  ) {
    errorMessages.push(
      "Pregnancy test date was outside the permitted date range"
    );
  }
  if (
    registerPatient.continueWithPregnancyTestDateIncomplete &&
    registerPatient.type === typeOfPAF.WOMANOFCP
  ) {
    errorMessages.push(
      errorMessagesConstants.PREGNANACY_TEST_DATE_INCOMPLETE_TITLE
    );
  }
  if (registerPatient.continueWithDateOfBirthUnderEighteen) {
    errorMessages.push(
      "You selected 'Continue with off-label use for Patient Date Of Birth'"
    );
  }
  if (registerPatient.continueMaximumNumberOfCyclesExceeded) {
    errorMessages.push("Maximum number of cycles exceeded for this patient");
  }

  const maxWeeksStillExceeded =
    prescriptionField &&
    isMaxWeeksExceededPatientInitiation(registerPatient, prescriptionField);
  if (
    (displayWeeks === "weeks" || displayWeeks === "weeks_per_cycle") &&
    registerPatient.continueMaximumNumberOfWeeksExceeded &&
    maxWeeksStillExceeded
  ) {
    errorMessages.push("Maximum number of weeks exceeded for this patient");
  }

  const maxCapsStillExceeded =
    prescriptionField &&
    isMaxCapsulesExceededPatientInitiationWeeksSchedule(
      registerPatient,
      prescriptionField
    );
  if (
    displayWeeks === "weeks" &&
    registerPatient.continueMaximumNumberOfCapsulesExceeded &&
    maxCapsStillExceeded
  ) {
    errorMessages.push(
      formatOutsidePermittedQuantityTitle(
        registerPatient?.unit_label ?? registerPatient?.product_unit_label
      )
    );
  }

  if (registerPatient.continueCapsulePerCycleIncomplete) {
    errorMessages.push(
      formatCapsulesPerCycleIncompleteTitle(
        registerPatient?.unit_label ?? registerPatient?.product_unit_label
      )
    );
  }
  if (
    (displayWeeks === "cycle" || displayWeeks === "weeks_per_cycle") &&
    registerPatient.continueNumberOfCyclesIncomplete
  ) {
    errorMessages.push(errorMessagesConstants.NUMBER_OF_CYCLES_INCOMPLETE);
  }
  if (
    registerPatient.continueNumberOfWeeksIncomplete &&
    displayWeeks === "weeks"
  ) {
    errorMessages.push(errorMessagesConstants.NUMBER_OF_WEEKS_INCOMPLETE);
  }
  if (
    displayWeeks === "weeks" &&
    registerPatient.continueCapsulePerWeekIncomplete
  ) {
    errorMessages.push(
      formatCapsulesPerWeeksIncompleteTitle(
        registerPatient?.unit_label ?? registerPatient?.product_unit_label
      )
    );
  }
  if (
    displayWeeks === "weeks_per_cycle" &&
    (registerPatient.continueNumberOfCyclesIncomplete ||
      registerPatient.continueNumberOfWeeksIncomplete)
  ) {
    errorMessages.push("Number of supply weeks Incomplete");
  }

  return (
    <StyledModal>
      <Modal
        isOpen={openModalNonComplaint.show}
        onRequestClose={closeNonComplaint}
        style={customStyles}
        contentLabel="Non-Complaint Modal"
        overlayClassName="overlay-modal"
        shouldCloseOnOverlayClick={false}
      >
        <div
          className="wrapper-modal"
          data-testid="patient-initiation-noncompliant-modal"
        >
          <div className="image">
            <Warning />
          </div>
          <div
            className="main-text-error"
            data-testid="patient-initiation-noncompliant-title"
          >
            Non-Compliant PAF
          </div>
          {openModalNonComplaint.step === 1 ? (
            <React.Fragment>
              <div data-testid="patient-initiation-noncompliant-description">
                This PAF is non-compliant. Please amend the following details.{" "}
              </div>
              <div className="mt-4 mb-1">
                The following issues were identified:
              </div>
              <div
                className="noncompliant-issue-list"
                data-testid="patient-initiation-noncompliant-issues"
              >
                {(errorMessages || []).map((error, index) => (
                  <div
                    className="noncompliant-issue bold"
                    key={index}
                    data-testid={`patient-initiation-noncompliant-issue-${index}`}
                  >
                    - {error}
                  </div>
                ))}
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <p data-testid="patient-initiation-noncompliant-password-description">
                This PAF contains incomplete/non-compliant data. If you wish to
                proceed outside the terms of the {hcp?.name} PPP based on your
                clinical understanding of your patient's needs please re-enter
                your password to document this decision.
              </p>
              <label
                className="sr-only"
                htmlFor="patient-initiation-noncompliant-password"
                data-testid="patient-initiation-noncompliant-password-label"
              >
                Password
              </label>
              <input
                id="patient-initiation-noncompliant-password"
                className="input-rejection"
                type="password"
                onChange={(e) => handleChangeValidPassword(e)}
                name="validate_password"
                placeholder="Password"
                data-testid="patient-initiation-noncompliant-password-input"
              />

              {(errorMessageFromServer || []).map((item, index) => (
                <ErrorText key={index} item={item} />
              ))}
            </React.Fragment>
          )}
        </div>
        <div
          className="wrapper-buttons"
          data-testid="patient-initiation-noncompliant-actions"
        >
          <button
            className="cancel mr-6"
            onClick={closeNonComplaint}
            data-testid="patient-initiation-noncompliant-cancel-button"
          >
            Cancel
          </button>
          <button
            className="red-button"
            onClick={submitNonComplaint}
            data-testid="patient-initiation-noncompliant-submit-button"
          >
            {openModalNonComplaint.step === 1 ? "Continue" : "Submit"}
          </button>
        </div>
      </Modal>
    </StyledModal>
  );
};

NonComplaintPAF.propTypes = {
  openModalNonComplaint: PropTypes.object,
  closeNonComplaint: PropTypes.func,
  submitNonComplaint: PropTypes.func,
  registerPatient: PropTypes.object,
  prescriptionField: PropTypes.object,
  handleChangeValidPassword: PropTypes.func,
  errorMessageFromServer: PropTypes.array,
};

export default NonComplaintPAF;
